/**
 * Signals — a series whose y carries no information.
 *
 * For an SMA or an RSI, `y` **is** the value. For a signal it is a slot: the
 * data says only whether something fired and in which direction, and where the
 * marker lands is a layout question the data cannot answer. Everything in this
 * module is that layout question, which is why it is decided here and not on
 * the server — the answer depends on what else is in the pane, and the server's
 * indicator table is the same for everyone.
 *
 * The wire carries one column per signal, valued `0`, `+1` or `-1`. The **sign**
 * is the direction and is data; the **magnitude** is the lane and is written
 * here. Sending the lane to the server instead would put it in the chart
 * request, and `channel_key` hashes the request — so reordering two signals
 * would cost a rebuild and a refetch, and two charts wanting the same bars would
 * stop sharing a data channel.
 *
 * `0` is a value, not a gap. That is also what makes the encoding robust
 * through the transport: `readNumericColumn` reads Arrow's values buffer
 * without its validity bitmap, so a null would arrive as `0` regardless — an
 * encoding whose "nothing happened" already *is* zero cannot be damaged by it.
 *
 * See docs/features/signals.md.
 */

import type { Plot } from "@qufolio/model";

export type SignalDirection = "up" | "down";

/**
 * How a signal's values read — a property of the **indicator**, from the
 * catalog's `eventKind`, never a user setting and never stored on the plot.
 *
 * - `single` — an event: a non-zero value fired, its sign the direction, and
 *   `0` is "did not fire" and draws nothing. Every signal but the ones below.
 * - `dual` — a state that is always one of two things: `1` draws in the up
 *   colour, `0` in the down colour, and only a missing value (NaN, null) is
 *   blank. JAS Timing.
 */
/** `flag`: an event that only ever fires 1 — 0 is nothing. Read as `single`;
 * the difference is the editor's (one colour), not the renderer's. */
export type EventKind = "single" | "dual" | "flag";

/** What a signal is when the catalog says nothing, or has not arrived. */
export const DEFAULT_EVENT_KIND: EventKind = "single";

/** One marker: which bar, which way. `y` is decided by the anchor, not here. */
export interface SignalPoint {
  /** Bar index into the group's x column. */
  index: number;
  direction: SignalDirection;
}

/** Where one signal plot's markers go, once the pane is known. */
/**
 * The glyph a signal draws — contract `PlotStyle.markerShape`.
 *
 * Every shape but `triangle` is symmetric, so for those the up/down colours are
 * the only thing that says which way a bar fired.
 */
export type MarkerShape = NonNullable<NonNullable<Plot["style"]>["markerShape"]>;

/** What a signal draws when its plot names no shape. The owner's choice. */
export const DEFAULT_MARKER_SHAPE: MarkerShape = "circle";

/** Every shape, in the order a picker offers them. */
export const MARKER_SHAPES: readonly MarkerShape[] = [
  "circle",
  "triangle",
  "triangleDown",
  "arrowUp",
  "arrowDown",
  "square",
  "diamond",
  "cross",
  "x",
  "line",
  "column",
  "text",
];

/**
 * The shapes that are a point-marker glyph — every one but `column`.
 *
 * `column` is not a glyph: it is a bar, one bar wide and a whole lane tall, so
 * it is drawn by a column renderable rather than a sprite stamped at a point.
 */
export type GlyphShape = Exclude<MarkerShape, "column">;

/**
 * How far a column stops short of its lane's edge, in lanes, top and bottom.
 * Zero: full height, the owner's call on 2026-10-03 — it was 0.04 so two lanes
 * firing together read as two columns. Put each event in its own pane for that.
 */
export const SIGNAL_COLUMN_INSET = 0;

/**
 * What a signal actually draws for its stored shape and anchor.
 *
 * **`column` only means something in a lane.** A pane-anchored lane has a
 * height to fill; a bar-anchored signal sits an offset away from its bar, and a
 * column "the full height" of that would be a column over the candles. So a
 * bar-anchored `column` draws `square` — the nearest glyph, still solid, still
 * one per bar — and the stored value is left alone, so moving the signal back
 * into a strip brings the column back.
 */
export function effectiveSignalShape(
  shape: MarkerShape,
  anchor: "pane" | "series",
): MarkerShape {
  return shape === "column" && anchor === "series" ? "square" : shape;
}

/**
 * The glyph for a shape that must be drawn as a point marker — a scatter, which
 * has no lane. `column` falls back to `square` for the reason above.
 */
export function glyphOf(shape: MarkerShape): GlyphShape {
  return shape === "column" ? "square" : shape;
}

/**
 * The y extent of a `column` in lane `lane`, on the lane axis.
 *
 * Lane *k* owns `[k - 0.5, k + 0.5]` ({@link paneLaneRange} pads the strip by
 * exactly that half-lane), so a column filling it reads as the TC2000-style
 * strip, edge to edge ({@link SIGNAL_COLUMN_INSET} is zero).
 *
 * `base` becomes the column renderable's `zeroLineY` and `top` the y written on
 * every firing bar; the lane axis is flipped, which swaps which one is drawn
 * higher and changes nothing else.
 */
export function signalColumnSpan(lane: number): { base: number; top: number } {
  return { base: lane - 0.5 + SIGNAL_COLUMN_INSET, top: lane + 0.5 - SIGNAL_COLUMN_INSET };
}

export interface SignalLanePlan {
  plotId: string;
  /** 1-based. Lane 0 is the zero line and is deliberately never used. */
  lane: number;
  anchor: "pane" | "series";
  /**
   * The plot an `anchor: "series"` signal rides, already resolved — the default
   * applied, and a reference to a removed plot degraded to it. `undefined` for
   * a pane-anchored signal, which references nothing.
   */
  anchorRef?: string;
}

/** Is this plot drawn as markers rather than as a series of values? */
export function isSignalPlot(plot: Plot): boolean {
  return plot.type === "signal";
}

/**
 * Plot order within a pane: `order` when given, array position to break ties.
 *
 * Array position is meaningful in the contract (D8), so it is the tiebreak
 * rather than an id comparison — two plots with no `order` keep the order they
 * were added in, which is the one a reader put them in.
 */
function byOrder(plots: readonly Plot[]): Plot[] {
  return plots
    .map((plot, position) => ({ plot, position }))
    .sort(
      (a, b) =>
        (a.plot.order ?? Number.MAX_SAFE_INTEGER) - (b.plot.order ?? Number.MAX_SAFE_INTEGER) ||
        a.position - b.position,
    )
    .map((entry) => entry.plot);
}

/**
 * The plot a signal rides when it names none — the pane's OHLC plot if it has
 * one, else the first plot by order.
 *
 * This is what makes the price case need no configuration while a MACD pane has
 * to say whether it means the line or the histogram: in a price pane the answer
 * is obvious and there is exactly one candidate, and in an oscillator pane it is
 * a real choice with no default worth guessing.
 */
export function primaryPlotId(plots: readonly Plot[]): string | undefined {
  const drawable = byOrder(plots.filter((plot) => !isSignalPlot(plot)));
  const ohlc = drawable.find((plot) => plot.type === "candlestick" || plot.type === "ohlc");
  return (ohlc ?? drawable[0])?.id;
}

/**
 * Resolve one signal's anchor reference against the pane it is in.
 *
 * **A reference to a plot that is gone degrades to the default; it never
 * errors.** The same call the contract makes for a saved filter whose reference
 * was deleted — a signal that vanishes because someone removed the line it sat
 * near is a worse answer than one that moves.
 *
 * A signal may not ride another signal: the reference would have no value of its
 * own to sit beside, and a chain of them would have no base case.
 */
export function resolveAnchorRef(plot: Plot, plots: readonly Plot[]): string | undefined {
  if (plot.signal?.anchor !== "series") return undefined;

  const named = plot.signal.anchorRef;
  const target = named ? plots.find((candidate) => candidate.id === named) : undefined;
  if (target && !isSignalPlot(target)) return target.id;
  return primaryPlotId(plots);
}

/**
 * Lane per signal plot in one pane, in plot order, 1-based.
 *
 * Lanes are what "the anchor value changes when there is more than one" comes
 * to: the first signal sits one step from its anchor, the second two. Numbering
 * from one rather than zero keeps a pane-anchored lane off the axis' own zero
 * line, where it would read as part of the grid rather than as data.
 *
 * Signals sharing a pane share the numbering whatever they are anchored to, so
 * a pane holding one of each does not stack two things in the same place.
 */
export function assignSignalLanes(plots: readonly Plot[]): SignalLanePlan[] {
  // **A shared `row` takes the lane of its first plot.** Squeeze On and
  // Squeeze Fired are two plots — each keeps its own shape and colour — drawn
  // in one lane. Lanes stay dense: a joining plot takes no number of its own,
  // so the rows after it do not leave a gap where it would have been.
  const signals = byOrder(plots.filter(isSignalPlot));

  // **Laid out by level when every pane-anchored signal carries one**
  // (`PlotSignal.level`, 1 = the bottom row) — a family whose rows are spaced on
  // purpose, gaps included: JAS Pressure, Hindenburg 10 down to Defib 1 (owner,
  // 2026-10-03). The lane axis is flipped so lane 1 is the top row, so a level
  // becomes `top + 1 - level`. Anything short of every one keeps the packed
  // layout, so one stray level cannot scatter a strip.
  const paneAnchored = signals.filter((p) => (p.signal?.anchor ?? "pane") === "pane");
  const levels = paneAnchored.map((p) => p.signal?.level);
  if (paneAnchored.length > 0 && levels.every((l): l is number => typeof l === "number" && l >= 1)) {
    const top = Math.max(...levels);
    return signals.map((plot) => ({
      plotId: plot.id,
      lane: top + 1 - (plot.signal?.level ?? top),
      anchor: plot.signal?.anchor ?? "pane",
      anchorRef: resolveAnchorRef(plot, plots),
    }));
  }

  const rowLane = new Map<string, number>();
  let next = 0;
  return signals.map((plot) => {
    const row = plot.signal?.row;
    let lane = row !== undefined ? rowLane.get(row) : undefined;
    if (lane === undefined) {
      lane = ++next;
      if (row !== undefined) rowLane.set(row, lane);
    }
    return {
      plotId: plot.id,
      lane,
      anchor: plot.signal?.anchor ?? "pane",
      anchorRef: resolveAnchorRef(plot, plots),
    };
  });
}

/**
 * The bars a signal fired on, and which way.
 *
 * Zero is dropped because zero is "did not fire" — the one value a signal
 * column spends most of its length at. Non-finite is dropped too: an indicator
 * that produced no value cannot have produced an event, and `NaN > 0` is false
 * in a way that would otherwise silently classify it as a down event.
 */
export function signalPoints(
  values: readonly number[],
  kind: EventKind = DEFAULT_EVENT_KIND,
): SignalPoint[] {
  const out: SignalPoint[] = [];
  for (let index = 0; index < values.length; index++) {
    const direction = signalDirection(values[index], kind);
    if (direction) out.push({ index, direction });
  }
  return out;
}

/**
 * Which way one signal value fired, or `undefined` for "did not fire".
 *
 * The one reading of a signed value, shared by the bulk path above and the
 * realtime one below so the two cannot disagree about what a value means. A
 * hole reads as zero, which is already "did not fire", so the sparse-array case
 * needs no branch of its own.
 */
export function signalDirection(
  value: number | undefined,
  kind: EventKind = DEFAULT_EVENT_KIND,
): SignalDirection | undefined {
  // **Dual: zero is a value, and only absence is blank.** A hole is *not* read
  // as zero here — that would paint a down mark on a bar that has no reading.
  if (kind === "dual") {
    if (value === undefined || !Number.isFinite(value)) return undefined;
    return value > 0 ? "up" : "down";
  }
  const v = value ?? 0;
  if (!Number.isFinite(v) || v === 0) return undefined;
  return v > 0 ? "up" : "down";
}

/**
 * The marks with one bar set to what its value now says — the realtime half of
 * {@link signalPoints}.
 *
 * A refresh moves the live bar, and for a signal that changes **which marks
 * exist**, not a y: a bar that starts firing gains one, a bar that stops loses
 * it, a bar that reverses flips. Every other bar's mark is kept exactly as it
 * was. A new array is returned; the input is not touched.
 *
 * `make` builds the mark, because the caller's mark carries more than this
 * module knows about — the renderer keeps the bar's x on it.
 */
export function withSignalAt<M extends { index: number }>(
  marks: readonly M[],
  index: number,
  value: number | undefined,
  make: (direction: SignalDirection) => M,
  kind: EventKind = DEFAULT_EVENT_KIND,
): M[] {
  const kept = marks.filter((mark) => mark.index !== index);
  const direction = signalDirection(value, kind);
  return direction ? [...kept, make(direction)] : kept;
}

/**
 * How far one lane sits from the value it rides, as a fraction of the axis'
 * visible span.
 *
 * A fraction rather than a price, because the gap between a marker and the bar
 * it belongs to is a *visual* quantity. A fixed price offset is a different
 * number of pixels at every zoom level — invisible when zoomed out, a mile wide
 * when zoomed in — and the marker stops reading as attached to the bar, which is
 * the only thing that makes a bar anchor worth having.
 */
export const SIGNAL_GAP_FRACTION = 0.015;

/**
 * The offset for lane `lane` against a visible range.
 *
 * Zero for a range that is empty or not yet measured: markers then sit exactly
 * on the bar, which is wrong by a few pixels rather than thrown off the axis
 * entirely. A chart renders before its first range is settled, and NaN
 * propagating into a y would drop the point.
 */
export function signalOffset(range: { min: number; max: number }, lane: number): number {
  const span = range.max - range.min;
  if (!Number.isFinite(span) || span <= 0) return 0;
  return span * SIGNAL_GAP_FRACTION * lane;
}

/**
 * Where a bar-anchored marker sits: below the bar for an up event, above it for
 * a down one.
 *
 * `low`/`high` are the bar's extremes when the anchor is a candlestick or OHLC
 * plot, and both are the same number when it is an ordinary xy series — a line
 * has one value and the direction only picks which side of it to sit on. That
 * collapse is why one function serves both and the caller does not branch.
 *
 * An up marker goes **below**: the event is a breakout above the previous
 * week's high, so the marker points up *at* the bar from underneath. Putting it
 * above would have it pointing away from what it describes.
 */
export function anchoredSignalY(
  direction: SignalDirection,
  bar: { low: number; high: number },
  offset: number,
): number {
  return direction === "up" ? bar.low - offset : bar.high + offset;
}

/**
 * The fixed range for a pane-anchored signal strip's hidden axis.
 *
 * Fixed rather than auto-ranged: the numbers are lane indices, so a strip whose
 * axis fits its data would move every lane whenever a signal fired for the first
 * time, and a strip where nothing has fired yet would have no range at all. The
 * half-lane of padding keeps a marker's radius inside the pane instead of
 * clipped against its edge.
 */
export function paneLaneRange(laneCount: number): { min: number; max: number } {
  return { min: 0.5, max: Math.max(laneCount, 1) + 0.5 };
}
